import { Link } from 'react-router-dom';
import { ArrowRight, Plane, Award, Heart, Target } from 'lucide-react';
import SEO from '../components/SEO';

const CALENDLY_LINK = "https://calendly.com/nicolas-gate33/discovery-call";
const BOOK_PAGE_LINK = "https://gate33.co/book";

const AboutPage = () => {
  const journey = [
    {
      id: "commercial",
      phase: "Commercial Aviation",
      description: "Years of experience with Qatar Airways and Etihad Airways, delivering service excellence across commercial, premium, and charter environments.",
    },
    {
      id: "transition",
      phase: "The Transition",
      description: "The challenging journey into private aviation — navigating an industry with no clear pathway, learning the unwritten rules firsthand.",
    },
    {
      id: "private",
      phase: "Private Aviation",
      description: "Operating on business jets across owner flights, charter operations, and corporate missions, serving UHNW clients and understanding the true operational standards of private aviation.",
    },
    {
      id: "gate33",
      phase: "Gate 33",
      description: "Building the bridge that didn't exist — helping others transition with clarity, strategy, and real operational standards.",
    },
  ];

  const values = [
    {
      id: "direct",
      icon: Target,
      title: "Direct & Honest",
      description: "No fluff. No illusions. Only clear, actionable insight from real experience.",
    },
    {
      id: "excellence",
      icon: Award,
      title: "Excellence-Focused",
      description: "High standards aren't negotiable. We prepare you for what private aviation truly demands.",
    },
    {
      id: "invested",
      icon: Heart,
      title: "Genuinely Invested",
      description: "Your success matters. We're here to see you succeed, not just to sell a service.",
    },
  ];

  return (
    <div data-testid="about-page" className="min-h-screen">
      <SEO
        title="About — Founded by a working corporate flight attendant"
        description="Gate 33 was founded by Nicolas Perot dos Santos — Qatar Airways, Etihad, today corporate flight attendant on Bombardier Global and Challenger series aircraft."
        path="/about"
      />
      {/* Hero Section */}
      <section data-testid="about-hero" className="relative min-h-[70vh] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1768346564210-f382cdf18375?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBwcml2YXRlJTIwamV0JTIwY2FiaW4lMjBhaXNsZSUyMHNlYXRzJTIwZWxlZ2FudCUyMHBhbm9yYW1pY3xlbnwwfHx8fDE3NzU2ODU4NTd8MA&ixlib=rb-4.1.0&q=85"
            alt="Gate 33 private aviation training and consulting — About luxury jet cabin"
            title="Gate 33 private aviation training and consulting — About luxury jet cabin"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FDFBF7] via-[#FDFBF7]/90 to-[#FDFBF7]/40" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 py-24 w-full text-center">
          <div className="w-32 h-32 md:w-36 md:h-36 rounded-full overflow-hidden border-4 border-[#C5A880]/30 shadow-2xl mx-auto mb-8">
            <img
              src="https://customer-assets-eiarnc6j.emergentagent.net/job_gate33-preview/artifacts/wjigu3bs_Screenshot%202026-07-18%20at%2020.52.04.png"
              alt="Gate 33 private aviation training and consulting — Nicolas Perot dos Santos, founder"
              title="Gate 33 private aviation training and consulting — Nicolas Perot dos Santos, founder"
              className="w-full h-full object-cover object-center"
            />
          </div>
          <div className="max-w-3xl mx-auto">
            <span className="overline inline-block mb-4">About Gate 33</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight mb-6">
              Built from Real Experience — Not Theory
            </h1>
            <p className="text-lg text-[#5A5A5A] leading-relaxed">
              Founded by Nicolas Perot dos Santos, after years in commercial aviation with Qatar Airways and Etihad Airways,
              and firsthand experience in business aviation, Gate 33 was created to bridge the gap between ambition and reality.
            </p>
          </div>
        </div>
      </section>

      {/* The Story */}
      <section data-testid="about-story" className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <span className="overline inline-block mb-4">The Story</span>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-8">
              Why Gate 33 Exists
            </h2>
            
            <div className="prose prose-lg">
              <p className="text-[#5A5A5A] leading-relaxed mb-6">
                After years in commercial aviation with Qatar Airways and Etihad Airways, 
                the allure of private aviation was clear. The exclusivity, the service level, 
                the unique experiences — it represented the pinnacle of the profession.
              </p>
              <p className="text-[#5A5A5A] leading-relaxed mb-6">
                But the pathway wasn't. There was no clear route, no structured guidance, 
                no honest information about what operators actually expected. The industry 
                was selective, hidden, and difficult to penetrate.
              </p>
              <p className="text-[#5A5A5A] leading-relaxed mb-6">
                Making that transition required trial, error, and persistence. It took
                Nicolas 120 applications to break into business aviation — long before he
                truly understood what operators actually look for. It required
                understanding not just what to do, but how to position yourself, how to
                present your experience, and what standards truly mattered.
              </p>
              <p className="text-[#5A5A5A] leading-relaxed mb-6">
                That first-hand experience — the wasted applications, the misread expectations,
                and the eventual clarity — is exactly what Gate 33 was built to spare the next
                candidate.
              </p>
              <p className="text-[#1A1A1A] leading-relaxed font-medium">
                Gate 33 exists because that bridge should not be hidden. With the right guidance, 
                the right positioning, and a real understanding of the industry, the transition 
                becomes clear — and achievable.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About the Founder - NEW SECTION */}
      <section data-testid="about-founder" className="section-padding bg-[#FDFBF7]">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="lg:order-2">
              <span className="overline inline-block mb-4">About the Founder</span>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-6">
                Nicolas Perot dos Santos
              </h2>
              <p className="text-[#5A5A5A] leading-relaxed mb-6">
                Nicolas Perot dos Santos is a business aviation cabin crew professional with experience 
                across commercial and private aviation.
              </p>
              <p className="text-[#5A5A5A] leading-relaxed mb-6">
                After working with Qatar Airways and Etihad Airways, he transitioned into business aviation, 
                operating on high-end aircraft and serving ultra high-net-worth individuals.
              </p>
              <p className="text-[#5A5A5A] leading-relaxed mb-6">
                Through this experience, he developed a deep understanding of what truly differentiates 
                private aviation: autonomy, precision, discretion, and operational excellence.
              </p>
              <p className="text-[#1A1A1A] leading-relaxed font-medium">
                Gate 33 was created to bring that reality to others — with clarity, structure, 
                and real-world insight.
              </p>
            </div>
            <div className="lg:order-1">
              <div className="aspect-square bg-[#1A1A1A] flex items-center justify-center p-12">
                <div className="text-center">
                  <span className="font-heading text-7xl text-[#C5A880]">33</span>
                  <p className="text-sm text-[#9A9A9A] mt-4 uppercase tracking-wider">Gate to Excellence</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Gate 33 */}
      <section data-testid="about-why-gate33" className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="overline inline-block mb-4">The Name</span>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-6">
                Why Gate 33
              </h2>
              <p className="text-[#5A5A5A] leading-relaxed mb-6">
                Gate 33 is more than a name — it reflects both aviation reality and a deeper philosophy behind the brand.
              </p>
              <p className="text-[#5A5A5A] leading-relaxed mb-4">
                In aviation, a gate is the point of transition. 
                The moment where passengers move from the terminal into the aircraft — from intention into action.
              </p>
              <p className="text-[#1A1A1A] leading-relaxed font-medium mb-6">
                Gate 33 represents that threshold.
              </p>
              <p className="text-[#5A5A5A] leading-relaxed mb-6">
                A structured entry into private aviation, done the right way.
              </p>
              <p className="text-[#5A5A5A] leading-relaxed mb-4">
                The number 33 carries its own meaning. In many contexts, it symbolizes alignment, precision, and elevation — 
                a number often associated with mastery and higher standards.
              </p>
              <p className="text-[#5A5A5A] leading-relaxed mb-4">
                It also reflects a subtle French identity. +33 is the international dialing code of France — 
                a discreet nod to a culture of refined service and attention to detail.
              </p>
              <p className="text-[#1A1A1A] leading-relaxed font-medium">
                Together, Gate 33 stands for a clear idea: entering private aviation with structure, precision, 
                and the right level of standards.
              </p>
            </div>
            <div className="relative">
              <div className="aspect-square bg-[#F5F3EF] flex items-center justify-center">
                <div className="text-center p-12">
                  <span className="font-heading text-9xl text-[#C5A880]/30">33</span>
                  <div className="mt-6 space-y-2">
                    <p className="text-xs uppercase tracking-[0.2em] text-[#C5A880] font-semibold">Gate</p>
                    <p className="text-sm text-[#5A5A5A]">Structure. Precision. Standards.</p>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-6 -right-6 w-24 h-24 border border-[#C5A880] hidden lg:block" />
            </div>
          </div>
        </div>
      </section>


      {/* Journey Timeline */}
      <section data-testid="about-journey" className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="overline inline-block mb-4">The Journey</span>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight">
              A Path Built on Experience
            </h2>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-0">
              {journey.map((step, index) => (
                <div
                  key={step.id}
                  data-testid={`journey-step-${step.id}`}
                  className="grid grid-cols-12 gap-6"
                >
                  {/* Timeline Line */}
                  <div className="col-span-1 flex flex-col items-center">
                    <div className="w-4 h-4 border-2 border-[#C5A880] bg-[#FDFBF7] rounded-full z-10" />
                    {index < journey.length - 1 && (
                      <div className="w-px h-full bg-[#E5E0D8] flex-1" />
                    )}
                  </div>
                  {/* Content */}
                  <div className="col-span-11 pb-12">
                    <span className="overline inline-block mb-2">Phase {index + 1}</span>
                    <h3 className="font-heading text-xl mb-3">{step.phase}</h3>
                    <p className="text-[#5A5A5A] leading-relaxed">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section data-testid="about-values" className="section-padding bg-[#FDFBF7]">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="overline inline-block mb-4">Our Values</span>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-6">
              What We Stand For
            </h2>
            <p className="text-[#5A5A5A] leading-relaxed">
              Gate 33 isn't about volume. It's about quality guidance for 
              serious candidates ready to make a change.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value) => (
              <div
                key={value.id}
                data-testid={`value-card-${value.id}`}
                className="card-luxury text-center"
              >
                <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center border border-[#C5A880]">
                  <value.icon size={28} className="text-[#C5A880]" />
                </div>
                <h3 className="font-heading text-xl mb-3">{value.title}</h3>
                <p className="text-[#5A5A5A] text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Mission */}
      <section data-testid="about-mission" className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="aspect-square image-hover">
                <img
                  src="https://customer-assets.emergentagent.com/job_gate33-preview/artifacts/jk30h5vz_Global-Express-Charter-Interior-Front-View-scaled.jpg"
                  alt="Modern Bombardier Global cabin interior — the operational reality Gate 33 prepares candidates for"
                  title="Gate 33 — clear, honest, experience-based"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div>
              <span className="overline inline-block mb-4">The Mission</span>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-6">
                Clear, Honest, Experience-Based
              </h2>
              <p className="text-[#5A5A5A] leading-relaxed mb-6">
                Gate 33 exists to provide clear, honest, and experience-based guidance 
                for professionals entering private aviation.
              </p>
              <p className="text-[#5A5A5A] leading-relaxed mb-6">
                This is not about theory or generic advice. It's about understanding the real 
                expectations of operators, the reality of the role, and how to position yourself to succeed.
              </p>
              <div className="flex items-center gap-4">
                <Plane size={32} className="text-[#C5A880]" />
                <p className="font-heading text-xl italic">
                  "Your gateway to private aviation"
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Market Overview */}
      <section data-testid="about-market" className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="overline inline-block mb-4">The Market</span>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-6">
              A Growing Industry
            </h2>
            <p className="text-[#5A5A5A] leading-relaxed">
              Business aviation is expanding globally — creating unprecedented demand for trained, 
              operationally ready cabin crew.
            </p>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div data-testid="market-stat-fleet" className="bg-[#FDFBF7] border border-[#E5E0D8]/60 p-8 rounded-xl text-center">
              <span className="font-heading text-4xl sm:text-5xl text-[#C5A880] block">23,000+</span>
              <p className="text-sm text-[#5A5A5A] mt-3 font-medium">Active Business Jets</p>
              <p className="text-xs text-[#9A9A9A] mt-1">Worldwide fleet in operation</p>
            </div>
            <div data-testid="market-stat-orders" className="bg-[#FDFBF7] border border-[#E5E0D8]/60 p-8 rounded-xl text-center">
              <span className="font-heading text-4xl sm:text-5xl text-[#C5A880] block">$55B+</span>
              <p className="text-sm text-[#5A5A5A] mt-3 font-medium">In Aircraft Backlogs</p>
              <p className="text-xs text-[#9A9A9A] mt-1">OEM order books, and climbing</p>
            </div>
            <div data-testid="market-stat-catering" className="bg-[#FDFBF7] border border-[#E5E0D8]/60 p-8 rounded-xl text-center">
              <span className="font-heading text-4xl sm:text-5xl text-[#C5A880] block">$17B+</span>
              <p className="text-sm text-[#5A5A5A] mt-3 font-medium">Inflight Catering Market</p>
              <p className="text-xs text-[#9A9A9A] mt-1">And growing year over year</p>
            </div>
            <div data-testid="market-stat-crew" className="bg-[#FDFBF7] border border-[#E5E0D8]/60 p-8 rounded-xl text-center">
              <span className="font-heading text-4xl sm:text-5xl text-[#C5A880] block">102,000+</span>
              <p className="text-sm text-[#5A5A5A] mt-3 font-medium">New Aviation Professionals</p>
              <p className="text-xs text-[#9A9A9A] mt-1">Needed in business aviation by 2034</p>
            </div>
          </div>

          {/* Source footnote — this section gets shared and challenged */}
          <p className="text-center text-xs text-[#9A9A9A] mb-12 max-w-3xl mx-auto leading-relaxed">
            Sources: Honeywell 34<sup>th</sup> Global Business Aviation Outlook (Oct 2025), CAE Aviation Talent Forecast 2025, Global Jet Capital Q2 2025 OEM backlog report, Verified Market Research (in-flight catering $17.16B → $25.88B by 2030), Aviation Week / Airbus Corporate Jets fleet analysis.
          </p>

          {/* Insight text */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="bg-[#0F0F0F] p-8 rounded-xl">
              <h3 className="font-heading text-xl text-[#FDFBF7] mb-4">The Growth Picture</h3>
              <div className="space-y-3 text-sm text-[#A0A0A0]">
                <p>The global business jet fleet has grown consistently for two decades, with acceleration post-2020 as demand for private travel surged.</p>
                <p>Major manufacturers — Bombardier, Gulfstream, Dassault, Embraer — have record backlogs. New aircraft deliveries will require trained crew at a rate the industry has never seen.</p>
              </div>
            </div>
            <div className="bg-[#0F0F0F] p-8 rounded-xl">
              <h3 className="font-heading text-xl text-[#FDFBF7] mb-4">The Crew Gap</h3>
              <div className="space-y-3 text-sm text-[#A0A0A0]">
                <p>Every new aircraft requires trained cabin crew. With fleet expansion and natural attrition, the industry faces a growing talent shortage — particularly for crew who can operate autonomously at the level private aviation demands.</p>
                <p>This gap is where Gate 33 operates: preparing the next generation of business aviation professionals with real operational standards.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="about-cta" className="section-dark py-12 md:py-16 border-b border-[#C5A880]/40">
        <div className="container-custom text-center">
          <span className="text-xs uppercase tracking-[0.2em] font-medium text-[#C5A880] inline-block mb-4">
            Let's Connect
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mb-6 text-[#FDFBF7]">
            Ready to Start Your Journey?
          </h2>
          <p className="text-[#9A9A9A] max-w-xl mx-auto mb-10">
            Take the free 15-minute Readiness Assessment for a manually reviewed profile check —
            or book a discovery call to talk it through directly.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/operational-assessment"
              data-testid="about-final-cta-quiz"
              className="inline-flex items-center gap-2 bg-[#C5A880] text-[#0F0F0F] px-10 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#FDFBF7] transition-all duration-300"
            >
              Take the Readiness Assessment
              <ArrowRight size={16} />
            </Link>
            <a
              href={CALENDLY_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-final-cta"
              className="inline-flex items-center gap-2 border border-[#C5A880] text-[#C5A880] px-10 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#C5A880] hover:text-[#0F0F0F] transition-all duration-300"
            >
              Book a Discovery Call
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
